import { MAX_PER_ROW, ReportRow, WidgetLayout } from "../../../models/ReportBuilder";

/**
 * Layout rules for the report builder. A dashboard is an ordered list of rows;
 * a row is an ordered list of widget ids (at most MAX_PER_ROW) that share the
 * width equally. Every function here is pure and returns new rows; the store
 * applies them. There are no coordinates, so a drop can only mean one of three
 * things: a widget into a slot of a row, a widget into a new row between two
 * rows, or a whole row to another position.
 */

export type MoveDirection = "left" | "right" | "up" | "down";

/** Where a dragged thing would land. */
export type DropTarget =
	/** Widget into `row` at `index` (0..row length). */
	| { kind: "slot"; widgetId: string; rowId: string; index: number }
	/** Widget into a row of its own, inserted before the row at `rowIndex` (rows length = last). */
	| { kind: "new-row"; widgetId: string; rowIndex: number }
	/** Whole row inserted before the row at `rowIndex`. */
	| { kind: "row"; rowId: string; rowIndex: number };

export const newRowId = (): string =>
	typeof crypto !== "undefined" && "randomUUID" in crypto
		? crypto.randomUUID()
		: `${Date.now()}-${Math.random()}`;

const row = (widgets: string[]): ReportRow => ({ id: newRowId(), widgets });

/** Rows without empty ones. */
export const pruneRows = (rows: ReportRow[]): ReportRow[] =>
	rows.filter((r) => r.widgets.length > 0);

/** Row and index of a widget, if placed. */
export function locate(
	rows: ReportRow[],
	widgetId: string
): { rowIndex: number; index: number } | undefined {
	for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
		const index = rows[rowIndex].widgets.indexOf(widgetId);
		if (index >= 0) return { rowIndex, index };
	}
	return undefined;
}

/** One widget per row, in the order given. */
export const resetToRows = (widgetIds: string[]): ReportRow[] => widgetIds.map((id) => row([id]));

/** Reading order of legacy grid layouts: top to bottom, then left to right. */
export const legacyOrder = <T extends { id: string; layout?: WidgetLayout }>(widgets: T[]): T[] =>
	[...widgets].sort(
		(a, b) => (a.layout?.y ?? 0) - (b.layout?.y ?? 0) || (a.layout?.x ?? 0) - (b.layout?.x ?? 0)
	);

/**
 * Repairs rows against the set of widgets that exist: unknown ids are
 * dropped, widgets missing from every row get a row of their own at the
 * bottom, duplicates keep their first place, empty rows go. Returns the same
 * array when nothing had to change.
 */
export function normalizeRows(rows: ReportRow[] | undefined, widgetIds: string[]): ReportRow[] {
	const known = new Set(widgetIds);
	const seen = new Set<string>();
	let changed = !rows;
	const cleaned = (rows ?? []).map((r) => {
		const widgets = r.widgets.filter((id) => {
			if (!known.has(id) || seen.has(id)) return false;
			seen.add(id);
			return true;
		});
		if (widgets.length !== r.widgets.length) changed = true;
		return widgets.length === r.widgets.length ? r : { ...r, widgets };
	});
	const missing = widgetIds.filter((id) => !seen.has(id));
	if (missing.length) changed = true;
	const withMissing = [...cleaned, ...resetToRows(missing)];
	const pruned = pruneRows(withMissing);
	if (pruned.length !== withMissing.length) changed = true;
	return changed ? pruned : (rows as ReportRow[]);
}

export type Placement =
	/** A full-width row at the bottom. */
	| { kind: "bottom" }
	/** Into the last row if it has room, else a new row at the bottom. */
	| { kind: "last-row" }
	/** Right after another widget in its row if there is room, else a new row below that row. */
	| { kind: "after"; widgetId: string };

/** Places a widget that is not yet in any row. */
export function insertWidget(
	rows: ReportRow[],
	widgetId: string,
	placement: Placement = { kind: "bottom" }
): ReportRow[] {
	if (placement.kind === "last-row") {
		const last = rows[rows.length - 1];
		if (last && last.widgets.length < MAX_PER_ROW) {
			return rows.map((r) => (r === last ? { ...r, widgets: [...r.widgets, widgetId] } : r));
		}
		return [...rows, row([widgetId])];
	}
	if (placement.kind === "after") {
		const at = locate(rows, placement.widgetId);
		if (at) {
			const target = rows[at.rowIndex];
			if (target.widgets.length < MAX_PER_ROW) {
				const widgets = [...target.widgets];
				widgets.splice(at.index + 1, 0, widgetId);
				return rows.map((r) => (r === target ? { ...r, widgets } : r));
			}
			const next = [...rows];
			next.splice(at.rowIndex + 1, 0, row([widgetId]));
			return next;
		}
	}
	return [...rows, row([widgetId])];
}

/** Rows without the widget; its row goes too when it was the last one there. */
export const removeWidget = (rows: ReportRow[], widgetId: string): ReportRow[] =>
	pruneRows(
		rows.map((r) =>
			r.widgets.includes(widgetId)
				? { ...r, widgets: r.widgets.filter((id) => id !== widgetId) }
				: r
		)
	);

/** Moves a widget into `rowId` at `index`; refused (rows returned as is) when that row is full. */
export function moveWidgetToSlot(
	rows: ReportRow[],
	widgetId: string,
	rowId: string,
	index: number
): ReportRow[] {
	const from = locate(rows, widgetId);
	const target = rows.find((r) => r.id === rowId);
	if (!from || !target) return rows;
	const sameRow = rows[from.rowIndex].id === rowId;
	if (!sameRow && target.widgets.length >= MAX_PER_ROW) return rows;
	// Take the widget out first; the index then refers to the remaining widgets.
	const without = target.widgets.filter((id) => id !== widgetId);
	let at = sameRow && index > from.index ? index - 1 : index;
	at = Math.max(0, Math.min(at, without.length));
	if (sameRow && at === from.index) return rows;
	const widgets = [...without];
	widgets.splice(at, 0, widgetId);
	return pruneRows(
		rows.map((r) =>
			r.id === rowId
				? { ...r, widgets }
				: r.widgets.includes(widgetId)
				? { ...r, widgets: r.widgets.filter((id) => id !== widgetId) }
				: r
		)
	);
}

/** Moves a widget into a row of its own, inserted before the row at `rowIndex` (rows length = last). */
export function moveWidgetToNewRow(
	rows: ReportRow[],
	widgetId: string,
	rowIndex: number
): ReportRow[] {
	const from = locate(rows, widgetId);
	if (!from) return rows;
	const alone = rows[from.rowIndex].widgets.length === 1;
	// Already a row of its own at that gap: nothing to do.
	if (alone && (rowIndex === from.rowIndex || rowIndex === from.rowIndex + 1)) return rows;
	const next = rows.map((r) =>
		r.widgets.includes(widgetId) ? { ...r, widgets: r.widgets.filter((id) => id !== widgetId) } : r
	);
	next.splice(Math.max(0, Math.min(rowIndex, next.length)), 0, row([widgetId]));
	return pruneRows(next);
}

/** Moves a whole row to before the row currently at `rowIndex` (rows length = last). */
export function moveRow(rows: ReportRow[], rowId: string, rowIndex: number): ReportRow[] {
	const from = rows.findIndex((r) => r.id === rowId);
	if (from < 0) return rows;
	if (rowIndex === from || rowIndex === from + 1) return rows;
	const next = [...rows];
	const [moved] = next.splice(from, 1);
	next.splice(rowIndex > from ? rowIndex - 1 : rowIndex, 0, moved);
	return next;
}

export function applyDrop(rows: ReportRow[], target: DropTarget): ReportRow[] {
	switch (target.kind) {
		case "slot":
			return moveWidgetToSlot(rows, target.widgetId, target.rowId, target.index);
		case "new-row":
			return moveWidgetToNewRow(rows, target.widgetId, target.rowIndex);
		case "row":
			return moveRow(rows, target.rowId, target.rowIndex);
	}
}

/** Whether the menu move in `direction` would change anything. */
export const canMove = (rows: ReportRow[], widgetId: string, direction: MoveDirection): boolean =>
	moveWidget(rows, widgetId, direction) !== rows;

/**
 * Menu fallback for dragging. Left/right swap with the neighbour in the row.
 * Up/down move the widget into the adjacent row when it has room, else make a
 * new row of it just above/below the neighbouring row. A widget alone in its
 * row with no neighbouring row stays put.
 */
export function moveWidget(
	rows: ReportRow[],
	widgetId: string,
	direction: MoveDirection
): ReportRow[] {
	const at = locate(rows, widgetId);
	if (!at) return rows;
	const current = rows[at.rowIndex];
	if (direction === "left" || direction === "right") {
		const index = at.index + (direction === "left" ? -1 : 1);
		if (index < 0 || index >= current.widgets.length) return rows;
		const widgets = [...current.widgets];
		[widgets[at.index], widgets[index]] = [widgets[index], widgets[at.index]];
		return rows.map((r) => (r === current ? { ...r, widgets } : r));
	}
	const step = direction === "up" ? -1 : 1;
	const neighbourIndex = at.rowIndex + step;
	const neighbour = rows[neighbourIndex];
	const alone = current.widgets.length === 1;
	if (!neighbour) {
		// Nothing above/below: a shared widget breaks out into its own row at the edge.
		return alone
			? rows
			: moveWidgetToNewRow(rows, widgetId, direction === "up" ? at.rowIndex : at.rowIndex + 1);
	}
	if (neighbour.widgets.length < MAX_PER_ROW) {
		return moveWidgetToSlot(rows, widgetId, neighbour.id, neighbour.widgets.length);
	}
	// Neighbour is full: land in a new row on its far side.
	if (alone) {
		return moveRow(rows, current.id, direction === "up" ? neighbourIndex : neighbourIndex + 1);
	}
	return moveWidgetToNewRow(
		rows,
		widgetId,
		direction === "up" ? neighbourIndex : neighbourIndex + 1
	);
}

/** Row placements for widgets created one after another with S/M/L sizes: S=1, M=2, L=4 shares of a row. */
export function packPlacements(sizes: Array<"S" | "M" | "L">): Placement[] {
	const share = { S: 1, M: 2, L: MAX_PER_ROW };
	let used = 0;
	return sizes.map((size) => {
		const w = share[size];
		if (used > 0 && used + w <= MAX_PER_ROW) {
			used += w;
			return { kind: "last-row" };
		}
		used = w;
		return { kind: "bottom" };
	});
}
